/**
 * LeaveWorkspaceDialog Component
 * 
 * Dialog for leaving a workspace (non-owners only).
 */

import { Typography, Box, TextField } from "@mui/material";
import StandardDialog from "../../../components/StandardDialog";
import { Workspace } from "../../../types";

export interface LeaveWorkspaceDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  workspace: Workspace;
  leaveWorkspaceName: string;
  setLeaveWorkspaceName: (name: string) => void;
  leaving: boolean;
}

export default function LeaveWorkspaceDialog({
  open,
  onClose,
  onConfirm,
  workspace,
  leaveWorkspaceName,
  setLeaveWorkspaceName,
  leaving,
}: LeaveWorkspaceDialogProps) {
  const handleClose = (): void => {
    if (!leaving) {
      setLeaveWorkspaceName("");
      onClose();
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Leave Workspace"
      confirmText={leaving ? "Leaving..." : "Leave Workspace"}
      onConfirm={onConfirm}
      confirmDisabled={leaving || leaveWorkspaceName.trim() !== workspace.name.trim()}
      confirmColor="warning"
      disableClose={leaving}
      alert={{
        severity: "info",
        message: "Your data will be preserved. You can be added back to the workspace later.",
      }}
    >
      <Typography sx={{ textAlign: "center", mb: 2 }}>
        You're about to leave this workspace. Your transactions, accounts, and all data you added will be preserved.
        If you're added back to the workspace later, everything will continue working as before.
      </Typography>
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          To confirm, please type <strong>{workspace.name}</strong> in the field below:
        </Typography>
        <TextField
          fullWidth
          value={leaveWorkspaceName}
          onChange={(e) => setLeaveWorkspaceName(e.target.value)}
          placeholder="Type the workspace name to confirm"
          autoFocus
          error={leaveWorkspaceName.trim() !== "" && leaveWorkspaceName.trim() !== workspace.name.trim()}
          helperText={
            leaveWorkspaceName.trim() !== "" && leaveWorkspaceName.trim() !== workspace.name.trim()
              ? "The name does not match"
              : ""
          }
          disabled={leaving}
        />
      </Box>
    </StandardDialog>
  );
}
